import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Heart,
  Package,
  Trash2,
  ShoppingBag,
  ArrowRight,
  Loader2,
} from "lucide-react";
import { useWishlist } from "../../context/WishlistContext";

// Helper function to get full image URL
const getFullImageUrl = (path) => {
  if (!path) return null;

  // If it's already a full URL, return as is
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }

  // If it starts with /uploads, prepend the API base URL
  if (path.startsWith("/uploads")) {
    const apiBaseUrl =
      import.meta.env.VITE_API_UPLAODS || "http://localhost:3000";
    return `${apiBaseUrl}${path}`;
  }

  // If it's a relative path without /uploads, add it
  if (!path.startsWith("/")) {
    const apiBaseUrl =
      import.meta.env.VITE_API_UPLAODS || "http://localhost:3000";
    return `${apiBaseUrl}/${path}`;
  }

  return path;
};

// Product Image Component for BLOB handling
const ProductImage = ({ product, className }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const loadImage = () => {
      // Priority 1: mainImagePath (new path-based storage)
      if (product?.mainImagePath) {
        setImageUrl(getFullImageUrl(product.mainImagePath));
        setImageLoading(false);
        return;
      }

      // Priority 2: gallery images path
      if (product?.galleryImages && product.galleryImages.length > 0) {
        const firstImage = product.galleryImages[0];
        if (firstImage?.path) {
          setImageUrl(getFullImageUrl(firstImage.path));
          setImageLoading(false);
          return;
        }
        if (firstImage?.data) {
          setImageUrl(`data:${firstImage.mimeType};base64,${firstImage.data}`);
          setImageLoading(false);
          return;
        }
      }

      // Priority 3: imageBase64 (legacy BLOB)
      if (product?.imageBase64) {
        setImageUrl(product.imageBase64);
        setImageLoading(false);
        return;
      }

      // Priority 4: image URL
      if (product?.image) {
        setImageUrl(getFullImageUrl(product.image));
        setImageLoading(false);
        return;
      }

      setImageLoading(false);
    };

    loadImage();
  }, [product]);

  if (imageLoading && !imageError) {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        <Loader2 size={32} className="animate-spin text-primary-500" />
      </div>
    );
  }

  if (imageUrl && !imageError) {
    return (
      <img
        src={imageUrl}
        alt={product?.imageAlt || product?.name || "Product image"}
        className={`object-cover ${className}`}
        onError={() => {
          console.error(`Failed to load product image: ${imageUrl}`);
          setImageError(true);
          setImageLoading(false);
        }}
      />
    );
  }

  return (
    <div
      className={`flex items-center justify-center ${className} bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900`}
    >
      <Package size={48} className="text-gray-400" />
    </div>
  );
};

const UserWishlist = () => {
  const { wishlistItems, removeFromWishlist, clearWishlist } = useWishlist();
  const [removingId, setRemovingId] = useState(null);

  const handleRemove = async (productId) => {
    setRemovingId(productId);
    // Simulate slight delay for animation
    await new Promise((resolve) => setTimeout(resolve, 300));
    removeFromWishlist(productId);
    setRemovingId(null);
  };

  if (wishlistItems.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-3xl flex items-center justify-center mx-auto mb-6">
          <Heart size={48} className="text-gray-400" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
          Your Wishlist is Empty
        </h2>
        <p className="text-gray-500 dark:text-gray-400 mb-8 max-w-md mx-auto">
          Save your favorite products here and they'll appear in your wishlist.
        </p>
        <Link
          to="/products"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-medium hover:shadow-lg transition-all"
        >
          Browse Products
          <ArrowRight size={18} />
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            My Wishlist
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {wishlistItems.length} product
            {wishlistItems.length !== 1 ? "s" : ""} saved
          </p>
        </div>
        <button
          onClick={clearWishlist}
          className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
        >
          <Trash2 size={16} />
          <span className="text-sm">Clear All</span>
        </button>
      </div>

      {/* Wishlist Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence mode="popLayout">
          {wishlistItems.map((product, index) => (
            <motion.div
              key={product.id}
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-800/50 overflow-hidden hover:shadow-xl transition-all duration-300 group"
            >
              {/* Image Container */}
              <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900">
                <ProductImage
                  product={product}
                  className="w-full h-full group-hover:scale-110 transition-transform duration-500"
                />

                {/* Badges */}
                <div className="absolute top-3 left-3 flex flex-col gap-2">
                  {product.isNew && (
                    <span className="px-2 py-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-bold rounded-lg shadow-lg">
                      NEW
                    </span>
                  )}
                  {product.isFeatured && (
                    <span className="px-2 py-1 bg-gradient-to-r from-amber-500 to-orange-600 text-white text-xs font-bold rounded-lg shadow-lg">
                      FEATURED
                    </span>
                  )}
                  {!product.isAvailable && (
                    <span className="px-2 py-1 bg-gradient-to-r from-gray-500 to-gray-600 text-white text-xs font-bold rounded-lg shadow-lg">
                      OUT OF STOCK
                    </span>
                  )}
                </div>

                {/* Remove Button */}
                <button
                  onClick={() => handleRemove(product.id)}
                  disabled={removingId === product.id}
                  className="absolute top-3 right-3 p-2 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm rounded-xl hover:bg-red-500 hover:text-white transition-all shadow-lg disabled:opacity-50"
                >
                  {removingId === product.id ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Trash2 size={16} />
                  )}
                </button>
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white line-clamp-1 text-base">
                      {product.name}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {product.category === "food"
                        ? "Food & Beverages"
                        : "Non-Food Products"}{" "}
                      • SKU: {product.sku}
                    </p>
                  </div>
                  {product.isAvailable && (
                    <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 text-xs font-medium rounded-full whitespace-nowrap ml-2">
                      In Stock
                    </span>
                  )}
                </div>

                {/* Description */}
                <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-4">
                  {product.shortDescription ||
                    product.description ||
                    "No description available"}
                </p>

                {/* Actions */}
                <div className="flex gap-3">
                  <Link
                    to={`/products/${product.slug}`}
                    className="flex-1 text-center py-2.5 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white text-sm font-medium rounded-xl transition-all"
                  >
                    View Details
                  </Link>
                  <Link
                    to={`/inquiry?productId=${product.id}`}
                    className="px-4 py-2.5 border border-gray-200 dark:border-gray-700 hover:border-primary-500 rounded-xl transition-all group/btn"
                  >
                    <ShoppingBag
                      size={18}
                      className="text-gray-600 dark:text-gray-400 group-hover/btn:text-primary-600 transition-colors"
                    />
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default UserWishlist;
